/**
 * Fluidité B2 (P2#17) — une seule entrée « Importer » qui demande quoi importer.
 *
 * Avant : 7 boutons dispersés (en-tête « Importer », Conception « Importer une DPGF
 * (Word) », DPGF « Importer Excel » et « Générer … depuis docs », Marchés « Importer
 * DPGF » et « Uploader un devis »…) sans dire par où commencer. Désormais, l'en-tête,
 * Conception, Marchés et la DPGF ouvrent tous cette même fenêtre ; chaque choix
 * relance la fonction existante (mêmes fenêtres, mêmes routes serveur) :
 *
 *   DPGF (Word)            → POST /chantiers/{id}/dce/import        (archi, chef de projet)
 *   DPGF (Excel)           → DPGFImportDialog  (POST /dpgf/import)   (droit « Modifier le DPGF »)
 *   DPGF depuis documents  → DPGFGenerateAIDialog                    (droit « Modifier le DPGF »)
 *   Devis d'une entreprise → DevisUploadDialog (POST /devis/upload)  (maîtrise d'œuvre)
 *   Chantier déjà commencé → OnboardingWizard                        (fiche du chantier)
 *   Comptes rendus (PDF)   → ImportExternalCRCDialog                 (maîtrise d'œuvre)
 *   Conversation WhatsApp  → ImportWhatsAppDialog                    (cabinet)
 *   Documents et plans     → onglet Documents (zone de dépôt)
 *
 * Seuls les choix autorisés pour la personne sont proposés (`droits`) ; `choix`
 * restreint la liste (ex. dans la DPGF : Excel et documents seulement) ; `gestionnaires`
 * laisse l'écran appelant traiter lui-même un choix (la DPGF garde son import Excel).
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileDoc, FileXls, Sparkle, FilePdf, Buildings, ClipboardText, WhatsappLogo, Files, CaretRight,
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import DPGFImportDialog from './e/DPGFImportDialog';
import DevisUploadDialog from './e/DevisUploadDialog';
import DPGFGenerateAIDialog from './DPGFGenerateAIDialog';
import OnboardingWizard from './OnboardingWizard';
import ImportExternalCRCDialog from './crc/ImportExternalCRCDialog';
import ImportWhatsAppDialog from './ImportWhatsAppDialog';
import { IMPORTS } from '../../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Ordre du chantier : conception, marchés, chantier, documents.
export const CHOIX_IMPORT = [
  { cle: 'dpgfWord', libelle: IMPORTS.dpgfWord, detail: 'Fichier .docx : les lots et les postes arrivent dans la DPGF.', icon: FileDoc },
  { cle: 'dpgfExcel', libelle: IMPORTS.dpgfExcel, detail: 'Fichier .xlsx : les lots et les postes arrivent dans la DPGF.', icon: FileXls },
  { cle: 'dpgfDocuments', libelle: IMPORTS.dpgfDocuments, detail: "L'assistant propose des lots à partir des pièces déposées ; vous choisissez ce que vous gardez.", icon: Sparkle },
  { cle: 'devis', libelle: IMPORTS.devis, detail: 'Le montant et les postes sont lus puis rattachés au lot.', icon: FilePdf },
  { cle: 'chantierEnCours', libelle: IMPORTS.chantierEnCours, detail: 'Pour reprendre un chantier en cours : étape, lots attribués, factures déjà payées.', icon: Buildings },
  { cle: 'comptesRendus', libelle: IMPORTS.comptesRendus, detail: 'Les anciens comptes rendus rejoignent la liste du chantier.', icon: ClipboardText },
  { cle: 'whatsapp', libelle: IMPORTS.whatsapp, detail: 'Export .zip ou .txt du groupe : les messages et photos rejoignent le Fil du chantier.', icon: WhatsappLogo },
  { cle: 'documents', libelle: IMPORTS.documents, detail: 'Déposer des fichiers dans Documents (plans avec leur indice).', icon: Files },
];

export default function ImporterDialog({
  open, onOpenChange, chantierId, chantier, droits = {}, choix = null, gestionnaires = {}, onImporte, onAllerDocuments,
}) {
  const [enCours, setEnCours] = useState(null); // clé de la fenêtre ouverte
  const [importWord, setImportWord] = useState(false);
  const wordRef = useRef(null);

  const proposes = CHOIX_IMPORT.filter((c) => (!choix || choix.includes(c.cle)) && droits[c.cle]);

  const fin = (cle) => { setEnCours(null); if (onImporte) onImporte(cle); };

  const choisir = (cle) => {
    onOpenChange(false);
    if (gestionnaires[cle]) { gestionnaires[cle](); return; }
    if (cle === 'dpgfWord') { wordRef.current?.click(); return; }
    if (cle === 'documents') { if (onAllerDocuments) onAllerDocuments(); return; }
    setEnCours(cle);
  };

  // Ex-« Importer une DPGF (Word) » de Conception (même route, même contrôle du format).
  const importerWord = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error('Fichier .docx requis');
      if (wordRef.current) wordRef.current.value = '';
      return;
    }
    setImportWord(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dce/import`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(data.message);
      fin('dpgfWord');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur import DPGF (Word)');
    } finally {
      setImportWord(false);
      if (wordRef.current) wordRef.current.value = '';
    }
  };

  return (
    <>
      <input ref={wordRef} type="file" accept=".docx" onChange={importerWord} className="hidden" data-testid="importer-dpgf-word-input" />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" data-testid="importer-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>{IMPORTS.titre}</DialogTitle>
            <DialogDescription>Choisissez ce que vous apportez : l'import se fait ensuite au bon endroit du chantier.</DialogDescription>
          </DialogHeader>
          {proposes.length === 0 ? (
            <p className="text-sm text-[#6B6B6B]" data-testid="importer-aucun">Aucun import n'est ouvert à votre rôle sur ce chantier.</p>
          ) : (
            <ul className="divide-y divide-[#F0F0ED] border border-[#E0E0DB]">
              {proposes.map((c) => {
                const Icon = c.icon;
                return (
                  <li key={c.cle}>
                    <button
                      type="button"
                      onClick={() => choisir(c.cle)}
                      disabled={c.cle === 'dpgfWord' && importWord}
                      className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-[#FAFAF8] disabled:opacity-50"
                      data-testid={`importer-choix-${c.cle}`}
                    >
                      <Icon size={22} className="text-[#C4773B] flex-shrink-0 mt-0.5" />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-[#2C2C2C]">
                          {c.cle === 'dpgfWord' && importWord ? 'Import en cours…' : c.libelle}
                        </span>
                        <span className="block text-xs text-[#6B6B6B] mt-0.5">{c.detail}</span>
                      </span>
                      <CaretRight size={16} className="text-[#9A9A9A] mt-1" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      {enCours === 'dpgfExcel' && (
        <DPGFImportDialog chantierId={chantierId} onClose={() => setEnCours(null)} onSuccess={() => fin('dpgfExcel')} />
      )}
      {enCours === 'devis' && (
        <DevisUploadDialog chantierId={chantierId} onClose={() => setEnCours(null)} onSuccess={() => fin('devis')} />
      )}
      <DPGFGenerateAIDialog
        open={enCours === 'dpgfDocuments'}
        onOpenChange={(o) => { if (!o) setEnCours(null); }}
        chantierId={chantierId}
        onImported={() => fin('dpgfDocuments')}
      />
      {enCours === 'chantierEnCours' && (
        <OnboardingWizard
          open
          onClose={() => setEnCours(null)}
          chantierId={chantierId}
          chantier={chantier}
          onComplete={() => { if (onImporte) onImporte('chantierEnCours'); }}
        />
      )}
      {enCours === 'comptesRendus' && (
        <ImportExternalCRCDialog
          chantierId={chantierId}
          onClose={() => setEnCours(null)}
          onSuccess={(result) => { toast.success(`CR n°${result?.numero || '?'} importé`); fin('comptesRendus'); }}
        />
      )}
      <ImportWhatsAppDialog
        open={enCours === 'whatsapp'}
        onOpenChange={(o) => { if (!o) setEnCours(null); }}
        chantierId={chantierId}
        onImported={() => { if (onImporte) onImporte('whatsapp'); }}
      />
    </>
  );
}
